import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { HealthBadges } from '@/components/HealthBadges';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Copy, ExternalLink, Server, Globe, Shield,
  HardDrive, MemoryStick, Clock, Terminal, Container, Network,
  AlertTriangle, RefreshCw, Info, BrainCircuit, Loader2,
  Activity, List, Cpu, Settings, Play, Square, RotateCw
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';

export default function ServerDetailPage() {
  const { assetId } = useParams();
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [server, setServer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({});
  
  // Advanced Monitoring State
  const [metrics, setMetrics] = useState([]);
  const [metricsRange, setMetricsRange] = useState('1h');
  const [events, setEvents] = useState([]);
  const [eventsMeta, setEventsMeta] = useState({ source: null, derived_from: null });
  const [processes, setProcesses] = useState([]);
  const [services, setServices] = useState([]);
  const [advLoading, setAdvLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('facts');

  // AI State
  const [aiOutput, setAiOutput] = useState('');
  const [aiRunning, setAiRunning] = useState(false);
  const [apiKey, setApiKey] = useState(localStorage.getItem('openrouter_key') || '');

  const runAiDiagnosis = async () => {
    setAiRunning(true);
    setAiOutput('Initializing CatPAw agent on remote server...\nThis may take a minute...');
    
    try {
      const api = authAxios();
      const res = await api.post('/playbooks/run', {
        playbook_id: 'catpaw-diagnose',
        asset_id: assetId,
        params: {
          baseurl: 'https://openrouter.ai/api/v1',
          model: 'openai/gpt-4o'
        }
      });
      
      if (res.data.status === 'success') {
        setAiOutput(res.data.stdout || 'No output returned');
        toast.success('Diagnosis complete');
      } else {
        setAiOutput(`Error: ${res.data.error || res.data.stderr || 'Unknown error'}\n\nStdout:\n${res.data.stdout || ''}`);
        toast.error('Diagnosis failed');
      }
    } catch (err) {
      console.error(err);
      if (err.response && err.response.data) {
          // If server returned 400/500 with JSON body, show it
          const detail = err.response.data.detail || err.response.data.error || JSON.stringify(err.response.data);
          setAiOutput(`Request failed: ${detail}`);
      } else {
          setAiOutput(`Request failed: ${err.message}`);
      }
      toast.error('Request failed');
    } finally {
      setAiRunning(false);
    }
  };

  const fetchServer = async () => {
    try {
      const api = authAxios();
      const res = await api.get(`/servers/${assetId}`);
      setServer(res.data);
      setMeta({
        alias: res.data.meta?.alias || '',
        purpose: res.data.meta?.purpose || '',
        owner: res.data.meta?.owner || '',
        criticality: res.data.meta?.criticality || 'medium',
        notes: res.data.meta?.notes || '',
      });
      // Fetch advanced metrics in background
      fetchAdvancedData(api);
    } catch (err) {
      toast.error('Server not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvancedData = async (api) => {
    try {
      setAdvLoading(true);
      const [mRes, eRes, pRes, sRes] = await Promise.all([
        api.get(`/servers/${assetId}/metrics?range=${metricsRange}`),
        api.get(`/servers/${assetId}/events`),
        api.get(`/servers/${assetId}/processes`),
        api.get(`/servers/${assetId}/services`)
      ]);
      setMetrics(mRes.data.data);
      setEvents(eRes.data.data);
      setEventsMeta({
        source: eRes.data?.source || null,
        derived_from: eRes.data?.derived_from || null,
      });
      setProcesses(pRes.data.data);
      setServices(sRes.data.data);
    } catch (err) {
      console.error("Failed to fetch advanced data", err);
    } finally {
      setAdvLoading(false);
    }
  };

  // Re-fetch metrics when range changes
  useEffect(() => {
    if (server) {
      const api = authAxios();
      api.get(`/servers/${assetId}/metrics?range=${metricsRange}`).then(res => {
        setMetrics(res.data.data);
      });
    }
  }, [metricsRange]);

  useEffect(() => { fetchServer(); }, [assetId]); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try {
      const api = authAxios();
      const res = await api.patch(`/servers/${assetId}/meta`, meta);
      setServer(res.data);
      toast.success('Meta updated');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const copySSH = async () => {
    const cmd = server?.links?.ssh || `ssh root@${server?.host}`;
    try {
      await navigator.clipboard.writeText(cmd);
      toast.success('SSH command copied');
    } catch {
      // Fallback for environments without clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = cmd;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('SSH command copied');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="server-detail-loading">
        <RefreshCw className="w-8 h-8 text-[#EF694C] animate-spin" />
      </div>
    );
  }

  if (!server) return null;

  const audit = server.audit || {};

  return (
    <div className="space-y-8" data-testid="server-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/')}
          className="w-fit text-[#a1a1aa] hover:text-white hover:bg-white/5"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              (server.health?.issues_count || 0) > 0 ? 'bg-[#F43F5E]/10' : 'bg-[#10B981]/10'
            }`}>
              <Server className={`w-6 h-6 ${
                (server.health?.issues_count || 0) > 0 ? 'text-[#F43F5E]' : 'text-[#10B981]'
              }`} strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">
                {server.meta?.alias || server.asset_id}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm font-mono text-[#52525b]">{server.asset_id}</span>
                <Badge className="bg-white/5 text-[#a1a1aa] border-white/10 text-xs">{server.provider}</Badge>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="copy-ssh-btn"
            onClick={copySSH}
            className="h-11 px-4 bg-[#18181b] border border-white/10 text-white hover:bg-white/5 rounded-lg transition-all"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy SSH
          </Button>
          {server.links?.panel && (
            <Button
              data-testid="open-panel-btn"
              onClick={() => window.open(server.links.panel, '_blank')}
              className="h-11 px-4 bg-[#EF694C] hover:bg-[#d65d43] text-white rounded-lg shadow-[0_0_15px_rgba(239,105,76,0.3)] transition-all"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Panel
            </Button>
          )}
        </div>
      </div>

      {/* Health Summary & Score */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 flex items-center gap-3 bg-[#121214] p-4 rounded-xl border border-white/10">
          <HealthBadges health={server.health} />
        </div>
        <div className="bg-[#121214] p-4 rounded-xl border border-white/10 flex items-center justify-between">
          <div>
            <p className="text-xs text-[#a1a1aa] uppercase tracking-wider font-semibold">Health Score</p>
            <p className="text-xs text-[#52525b] mt-1">Overall system status</p>
          </div>
          <div className="relative flex items-center justify-center w-16 h-16">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
              <path
                className="text-white/5"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              />
              <path
                className={`${server.health?.score >= 80 ? 'text-[#10B981]' : server.health?.score >= 50 ? 'text-[#F59E0B]' : 'text-[#F43F5E]'}`}
                strokeDasharray={`${server.health?.score || 0}, 100`}
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute text-sm font-bold text-white">
              {server.health?.score || 0}%
            </div>
          </div>
        </div>
      </div>

      {/* Domain Overrides Warning */}
      {server.domain_overrides?.length > 0 && (
        <Card className="bg-[#F59E0B]/5 border-[#F59E0B]/20 rounded-xl">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#F59E0B]">Domain Owner Overrides</p>
              {server.domain_overrides.map((ov, i) => (
                <p key={i} className="text-xs text-[#a1a1aa] mt-1">
                  <span className="font-mono text-white">{ov.domain}</span> — {ov.note} ({ov.type})
                </p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#121214] border border-white/10 p-1 rounded-lg flex flex-wrap h-auto">
          <TabsTrigger data-testid="tab-facts" value="facts" className="data-[state=active]:bg-[#EF694C]/10 data-[state=active]:text-[#EF694C] rounded-md">
            Facts
          </TabsTrigger>
          <TabsTrigger data-testid="tab-metrics" value="metrics" className="data-[state=active]:bg-[#EF694C]/10 data-[state=active]:text-[#EF694C] rounded-md">
            <Activity className="w-4 h-4 mr-2" />
            Metrics
          </TabsTrigger>
          <TabsTrigger data-testid="tab-events" value="events" className="data-[state=active]:bg-[#EF694C]/10 data-[state=active]:text-[#EF694C] rounded-md">
            <List className="w-4 h-4 mr-2" />
            Events
          </TabsTrigger>
          <TabsTrigger data-testid="tab-processes" value="processes" className="data-[state=active]:bg-[#EF694C]/10 data-[state=active]:text-[#EF694C] rounded-md">
            <Cpu className="w-4 h-4 mr-2" />
            Processes
          </TabsTrigger>
          <TabsTrigger data-testid="tab-services" value="services" className="data-[state=active]:bg-[#EF694C]/10 data-[state=active]:text-[#EF694C] rounded-md">
            <Settings className="w-4 h-4 mr-2" />
            Services
          </TabsTrigger>
          <TabsTrigger data-testid="tab-meta" value="meta" className="data-[state=active]:bg-[#EF694C]/10 data-[state=active]:text-[#EF694C] rounded-md">
            Meta
          </TabsTrigger>
          <TabsTrigger data-testid="tab-certs" value="certs" className="data-[state=active]:bg-[#EF694C]/10 data-[state=active]:text-[#EF694C] rounded-md">
            Certificates
          </TabsTrigger>
          <TabsTrigger data-testid="tab-network" value="network" className="data-[state=active]:bg-[#EF694C]/10 data-[state=active]:text-[#EF694C] rounded-md">
            Network
          </TabsTrigger>
          <TabsTrigger data-testid="tab-ai" value="ai" className="data-[state=active]:bg-[#EF694C]/10 data-[state=active]:text-[#EF694C] rounded-md">
            <BrainCircuit className="w-4 h-4 mr-2" />
            AI Diagnostics
          </TabsTrigger>
        </TabsList>

        {/* Facts Tab */}
        <TabsContent value="facts" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
            {/* System Info */}
            <FactCard icon={Terminal} title="System" testId="fact-system">
              <FactRow label="OS" value={audit.os || 'N/A'} />
              <FactRow label="Kernel" value={audit.kernel || 'N/A'} />
              <FactRow label="Hostname" value={audit.hostname || 'N/A'} mono />
              <FactRow label="FQDN" value={server.primary_fqdn} mono />
              <FactRow label="Host IP" value={server.host} mono />
            </FactCard>

            {/* Uptime & Resources */}
            <FactCard icon={Clock} title="Resources" testId="fact-resources">
              <FactRow label="Uptime" value={audit.uptime || 'N/A'} />
              <FactRow label="Memory" value={audit.memory_used_pct || 'N/A'} highlight={server.health?.high_memory} />
              
              <div className="flex justify-between items-center py-1">
                <span className="text-sm text-[#52525b]">Swap</span>
                <span className="text-sm text-white">
                  {audit.swap_used_pct ? `${audit.swap_used_pct} (of ${audit.swap_total || 'unknown'})` : 'Disabled'}
                </span>
              </div>

              <div className="space-y-1 mt-2">
                <span className="text-xs text-[#a1a1aa]">Storage (Used / Total)</span>
                {Object.entries(audit.storage_pct || {}).map(([mount, pct]) => {
                  const pctVal = parseFloat(pct);
                  const totalGb = audit.storage_total_gb ? audit.storage_total_gb[mount] : null;
                  const usedGb = audit.storage_used_gb ? audit.storage_used_gb[mount] : null;
                  return (
                    <div key={mount} className="mb-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-mono text-[#52525b] truncate max-w-[100px]">{mount}</span>
                        <span className={`text-sm font-mono ${
                          pctVal >= 80 ? 'text-[#F43F5E]' : 'text-white'
                        }`}>
                          {totalGb ? (
                            `${pct} (${usedGb ?? '?'}GB / ${totalGb}GB)`
                          ) : (
                            `${pct} (Absolutwerte nicht verfügbar)`
                          )}
                        </span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${pctVal >= 80 ? 'bg-red-500' : 'bg-emerald-500'}`} 
                          style={{ width: pct }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </FactCard>

            {/* Containers */}
            <FactCard icon={Container} title="Containers" testId="fact-containers" count={audit.containers?.length || 0}>
              {(audit.containers || []).length === 0 ? (
                <p className="text-sm text-[#52525b]">No containers found</p>
              ) : (
                (audit.containers || []).map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-1 border-b border-white/5 last:border-0">
                    <span className="text-sm font-mono text-white truncate max-w-[180px]">{c.name}</span>
                    <Badge className={`text-[10px] ${
                      c.status?.startsWith('Up') ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' : 'bg-[#F43F5E]/10 text-[#F43F5E] border-[#F43F5E]/20'
                    }`}>{c.status}</Badge>
                  </div>
                ))
              )}
            </FactCard>

            {/* Failed Units */}
            <FactCard icon={AlertTriangle} title="Failed Units" testId="fact-failed-units" count={audit.failed_units?.length || 0}>
              {(audit.failed_units || []).length === 0 ? (
                <p className="text-sm text-[#10B981]">All units healthy</p>
              ) : (
                (audit.failed_units || []).map((u, i) => (
                  <div key={i} className="flex items-center gap-2 py-1">
                    <div className="w-2 h-2 rounded-full bg-[#F43F5E]" />
                    <span className="text-sm font-mono text-[#F43F5E]">{u}</span>
                  </div>
                ))
              )}
            </FactCard>

            {/* Roles */}
            <FactCard icon={Shield} title="Roles" testId="fact-roles" count={server.roles_guess?.length || 0}>
              <div className="flex flex-wrap gap-2">
                {(server.roles_guess || []).map(r => (
                  <Badge key={r} className="bg-[#EF694C]/10 text-[#EF694C] border-[#EF694C]/20 text-xs">
                    {r}
                  </Badge>
                ))}
                {(server.roles_guess || []).length === 0 && (
                  <p className="text-sm text-[#52525b]">No roles detected</p>
                )}
              </div>
            </FactCard>

            {/* Audit Info */}
            <FactCard icon={Info} title="Audit Info" testId="fact-audit-info">
              <FactRow label="Audit Date" value={audit.audit_date || 'N/A'} />
              <FactRow label="Quality" value={server.audit_quality || 'unknown'} />
              <FactRow label="Last Import" value={server.last_import ? new Date(server.last_import).toLocaleString() : 'N/A'} />
            </FactCard>
          </div>
        </TabsContent>

        {/* Meta Tab */}
        {/* Metrics Tab */}
        <TabsContent value="metrics" className="mt-6 space-y-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-white font-heading">Server Load Metrics</h2>
            <div className="flex gap-2">
              {['1h', '24h', '7d', '30d'].map(r => (
                <button
                  key={r}
                  onClick={() => setMetricsRange(r)}
                  className={`px-3 py-1 rounded-md text-sm ${metricsRange === r ? 'bg-[#EF694C] text-white' : 'bg-[#121214] text-[#a1a1aa] hover:bg-white/5'} border border-white/10`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          {advLoading && !metrics.length ? <Loader2 className="w-6 h-6 animate-spin text-[#EF694C] mx-auto" /> : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* CPU Chart */}
              <div className="bg-[#121214] border border-white/10 rounded-xl p-5 h-80">
                <h3 className="text-sm font-semibold text-white mb-4">CPU Usage (%)</h3>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={metrics}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" vertical={false} />
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={(t) => new Date(t).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} 
                      stroke="#a1a1aa" 
                      fontSize={12} 
                    />
                    <YAxis stroke="#a1a1aa" fontSize={12} domain={[0, 100]} />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                      labelFormatter={(t) => new Date(t).toLocaleString()}
                    />
                    <ReferenceLine y={70} stroke="#F59E0B" strokeDasharray="3 3" />
                    <ReferenceLine y={90} stroke="#F43F5E" strokeDasharray="3 3" />
                    <Line type="monotone" dataKey="cpu" stroke="#EF694C" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {/* RAM Chart */}
              <div className="bg-[#121214] border border-white/10 rounded-xl p-5 h-80">
                <h3 className="text-sm font-semibold text-white mb-4">RAM Usage (%)</h3>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={metrics}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" vertical={false} />
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={(t) => new Date(t).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} 
                      stroke="#a1a1aa" 
                      fontSize={12} 
                    />
                    <YAxis stroke="#a1a1aa" fontSize={12} domain={[0, 100]} />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                      labelFormatter={(t) => new Date(t).toLocaleString()}
                    />
                    <ReferenceLine y={85} stroke="#F59E0B" strokeDasharray="3 3" />
                    <ReferenceLine y={95} stroke="#F43F5E" strokeDasharray="3 3" />
                    <Line type="monotone" dataKey="ram" stroke="#8b5cf6" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events" className="mt-6">
          <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-[#18181b]">
              <div>
                <h2 className="text-lg font-semibold text-white">System Events Log</h2>
                {eventsMeta?.source === 'audit_derived' && (
                  <div className="text-xs text-[#52525b] mt-0.5">
                    Basierend auf letztem Server-Audit{eventsMeta?.derived_from ? ` (${new Date(eventsMeta.derived_from).toLocaleString()})` : ''}
                  </div>
                )}
              </div>
              <Badge className="bg-[#EF694C]/20 text-[#EF694C]">{events.length} Events</Badge>
            </div>
            <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto">
              {events.map(evt => (
                <div key={evt.id} className="p-4 hover:bg-white/5 transition-colors flex items-start gap-4">
                  <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                    evt.level === 'critical' ? 'bg-red-500' :
                    evt.level === 'warning' ? 'bg-orange-500' : 'bg-blue-500'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-white">{evt.code}</span>
                      <span className="text-xs text-[#a1a1aa]">{new Date(evt.timestamp).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-[#d4d4d8] mb-2">{evt.message}</p>
                    {/* Solutions Area */}
                    {(evt.level === 'critical' || evt.level === 'warning') && (
                      <div className="mt-2 p-3 bg-red-500/5 border border-red-500/10 rounded-lg">
                        <p className="text-xs font-semibold text-red-400 mb-1">Suggested Actions:</p>
                        <ul className="text-xs text-[#a1a1aa] list-disc list-inside space-y-1">
                          {evt.code === 'MEM_HIGH' && (
                            <>
                              <li>Check memory-heavy processes in the <button onClick={() => setActiveTab('processes')} className="text-[#EF694C] hover:underline">Processes Tab</button>.</li>
                              <li>If persistent, consider upgrading RAM or configuring Swap via Action Center.</li>
                            </>
                          )}
                          {evt.code === 'SVC_CRASH' && (
                            <>
                              <li>Check application logs for details.</li>
                              <li>Restart service via the <button onClick={() => setActiveTab('services')} className="text-[#EF694C] hover:underline">Services Tab</button>.</li>
                            </>
                          )}
                          {evt.code === 'DISK_WARN' && (
                            <>
                              <li>Clear old log files or backups.</li>
                              <li>Run <code>docker system prune</code> if using Docker heavily.</li>
                            </>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Processes Tab */}
        <TabsContent value="processes" className="mt-6">
          <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-[#18181b]">
              <h2 className="text-lg font-semibold text-white">Top Processes</h2>
              <button className="flex items-center gap-2 text-sm text-[#EF694C] hover:text-[#f18b75] transition-colors">
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
            </div>
            <table className="w-full text-left text-sm">
              <thead className="bg-[#18181b] text-[#a1a1aa] border-b border-white/10">
                <tr>
                  <th className="p-3 font-medium">PID</th>
                  <th className="p-3 font-medium">User</th>
                  <th className="p-3 font-medium">CPU %</th>
                  <th className="p-3 font-medium">MEM %</th>
                  <th className="p-3 font-medium">Command</th>
                  <th className="p-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {processes.map(proc => (
                  <tr key={proc.pid} className="hover:bg-white/5">
                    <td className="p-3 font-mono text-white">{proc.pid}</td>
                    <td className="p-3 text-[#d4d4d8]">{proc.user}</td>
                    <td className={`p-3 font-mono ${proc.cpu > 50 ? 'text-red-400' : proc.cpu > 20 ? 'text-orange-400' : 'text-[#10B981]'}`}>{proc.cpu.toFixed(1)}</td>
                    <td className={`p-3 font-mono ${proc.mem > 20 ? 'text-orange-400' : 'text-[#10B981]'}`}>{proc.mem.toFixed(1)}</td>
                    <td className="p-3 font-mono text-xs text-[#a1a1aa] truncate max-w-xs">{proc.command}</td>
                    <td className="p-3 text-right">
                      <button className="px-2 py-1 bg-red-500/10 text-red-500 hover:bg-red-500/20 rounded text-xs transition-colors border border-red-500/20">
                        Kill
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="mt-6">
          <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10 bg-[#18181b]">
              <h2 className="text-lg font-semibold text-white">Service Management</h2>
              <p className="text-xs text-[#a1a1aa] mt-1">Manage systemd services directly</p>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {services.map(svc => (
                <div key={svc.name} className="bg-[#18181b] border border-white/5 rounded-lg p-4 flex flex-col justify-between">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${svc.status === 'active' ? 'bg-[#10B981]' : 'bg-[#F43F5E]'}`} />
                      <span className="font-mono text-sm text-white">{svc.name}</span>
                    </div>
                    <Badge className={svc.status === 'active' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#F43F5E]/10 text-[#F43F5E]'}>
                      {svc.state}
                    </Badge>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button className="p-1.5 bg-white/5 hover:bg-white/10 rounded text-white transition-colors" title="Start">
                      <Play className="w-4 h-4" />
                    </button>
                    <button className="p-1.5 bg-white/5 hover:bg-white/10 rounded text-white transition-colors" title="Stop">
                      <Square className="w-4 h-4" />
                    </button>
                    <button className="p-1.5 bg-white/5 hover:bg-white/10 rounded text-white transition-colors" title="Restart">
                      <RotateCw className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="meta" className="mt-6">
          <Card className="bg-[#121214] border-white/10 rounded-xl max-w-2xl">
            <CardHeader>
              <CardTitle className="text-white font-heading">Edit Server Meta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#a1a1aa]">Alias</label>
                <Input
                  data-testid="meta-alias"
                  value={meta.alias}
                  onChange={e => setMeta({ ...meta, alias: e.target.value })}
                  className="h-11 bg-[#18181b] border-white/10 focus:border-[#EF694C] text-white rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#a1a1aa]">Purpose</label>
                <Input
                  data-testid="meta-purpose"
                  value={meta.purpose}
                  onChange={e => setMeta({ ...meta, purpose: e.target.value })}
                  className="h-11 bg-[#18181b] border-white/10 focus:border-[#EF694C] text-white rounded-lg"
                  placeholder="e.g. Production web server"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#a1a1aa]">Owner</label>
                <Input
                  data-testid="meta-owner"
                  value={meta.owner}
                  onChange={e => setMeta({ ...meta, owner: e.target.value })}
                  className="h-11 bg-[#18181b] border-white/10 focus:border-[#EF694C] text-white rounded-lg"
                  placeholder="e.g. DevOps Team"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#a1a1aa]">Criticality</label>
                <Select value={meta.criticality} onValueChange={v => setMeta({ ...meta, criticality: v })}>
                  <SelectTrigger data-testid="meta-criticality" className="h-11 bg-[#18181b] border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-white/10">
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#a1a1aa]">Notes</label>
                <Textarea
                  data-testid="meta-notes"
                  value={meta.notes}
                  onChange={e => setMeta({ ...meta, notes: e.target.value })}
                  className="min-h-[100px] bg-[#18181b] border-white/10 focus:border-[#EF694C] text-white rounded-lg"
                  placeholder="Additional notes..."
                />
              </div>
              <Button
                data-testid="save-meta-btn"
                onClick={handleSave}
                disabled={saving}
                className="h-11 px-6 bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold rounded-lg shadow-[0_0_15px_rgba(239,105,76,0.3)] hover:shadow-[0_0_25px_rgba(239,105,76,0.5)] transition-all duration-200 active:scale-95"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Certs Tab */}
        <TabsContent value="certs" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger-children">
            {(audit.cert_expiries || []).length === 0 ? (
              <Card className="bg-[#121214] border-white/10 rounded-xl">
                <CardContent className="p-6 text-center text-[#52525b]">
                  No certificate data available
                </CardContent>
              </Card>
            ) : (
              (audit.cert_expiries || []).map((cert, i) => {
                const isExpired = !cert.valid || (cert.expiry && new Date(cert.expiry) < new Date());
                const isExpiring = !isExpired && cert.expiry && new Date(cert.expiry) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                const isAutoRenewHint = isExpiring && !!server?.health?.cert_expiring_soon_autorenew;
                
                // Check domain override
                const override = (server.domain_overrides || []).find(o => o.domain === cert.domain);

                return (
                  <Card
                    key={i}
                    data-testid={`cert-card-${i}`}
                    className={`bg-[#121214] border rounded-xl transition-colors ${
                      isExpired ? 'border-[#F43F5E]/30' : isExpiring ? 'border-[#F59E0B]/30' : 'border-white/10'
                    }`}
                  >
                    <CardContent className="p-6 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-mono text-white">{cert.domain}</span>
                        <Badge className={`text-xs ${
                          isExpired ? 'bg-[#F43F5E]/10 text-[#F43F5E] border-[#F43F5E]/20'
                          : isAutoRenewHint ? 'bg-white/5 text-[#a1a1aa] border-white/10'
                          : isExpiring ? 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20'
                          : 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20'
                        }`}>
                          {isExpired ? 'Expired' : isAutoRenewHint ? 'Expiring (Auto)' : isExpiring ? 'Expiring Soon' : 'Valid'}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between text-[#a1a1aa]">
                          <span>Expiry</span>
                          <span className="font-mono text-white">{cert.expiry ? new Date(cert.expiry).toLocaleDateString() : 'N/A'}</span>
                        </div>
                        <div className="flex justify-between text-[#a1a1aa]">
                          <span>Domains</span>
                          <span className="font-mono text-white">{cert.domains}</span>
                        </div>
                      </div>
                      {override && (
                        <div className="flex items-center gap-2 mt-2 p-2 bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-lg">
                          <Info className="w-3 h-3 text-[#F59E0B]" />
                          <span className="text-[10px] text-[#F59E0B]">{override.note} — Legacy/Cleanup</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Network Tab */}
        <TabsContent value="network" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
            {/* Ports */}
            <FactCard icon={Network} title="Open Ports" testId="fact-ports" count={audit.open_ports?.length || 0}>
              <div className="flex flex-wrap gap-2">
                {(audit.open_ports || []).map(p => (
                  <Badge key={p} className="bg-white/5 text-white border-white/10 text-xs font-mono">{p}</Badge>
                ))}
                {(audit.open_ports || []).length === 0 && <p className="text-sm text-[#52525b]">No ports detected</p>}
              </div>
            </FactCard>

            {/* Domains */}
            <FactCard icon={Globe} title="Domains" testId="fact-domains" count={audit.domains?.length || 0}>
              {(audit.domains || []).map((d, i) => {
                const override = (server.domain_overrides || []).find(o => o.domain === d);
                return (
                  <div key={i} className="flex items-center justify-between py-1 border-b border-white/5 last:border-0">
                    <span className="text-sm font-mono text-white">{d}</span>
                    {override && (
                      <Badge className="bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20 text-[9px]">override</Badge>
                    )}
                  </div>
                );
              })}
              {(audit.domains || []).length === 0 && <p className="text-sm text-[#52525b]">No domains</p>}
            </FactCard>

            {/* Proxies */}
            <FactCard icon={Network} title="Proxies" testId="fact-proxies" count={audit.proxies?.length || 0}>
              {(audit.proxies || []).map((p, i) => (
                <div key={i} className="py-1 border-b border-white/5 last:border-0">
                  <span className="text-xs font-mono text-[#a1a1aa] break-all">{p}</span>
                </div>
              ))}
              {(audit.proxies || []).length === 0 && <p className="text-sm text-[#52525b]">No proxy config</p>}
            </FactCard>
          </div>
        </TabsContent>

        {/* AI Diagnostics Tab */}
        <TabsContent value="ai" className="mt-6">
          <Card className="bg-[#121214] border-white/10 rounded-xl">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2 font-heading">
                <BrainCircuit className="text-[#EF694C]" />
                AI-Powered System Diagnosis
              </CardTitle>
              <p className="text-sm text-[#a1a1aa]">
                Uses <strong>CatPAw</strong> agent with <strong>Hunter Alpha</strong> (1T params) to analyze system health.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <Button 
                  onClick={runAiDiagnosis} 
                  disabled={aiRunning}
                  className="bg-[#EF694C] hover:bg-[#d65d43] text-white min-w-[200px] h-11"
                >
                  {aiRunning ? <Loader2 className="animate-spin w-4 h-4 mr-2" /> : <BrainCircuit className="w-4 h-4 mr-2" />}
                  {aiRunning ? 'Running Analysis...' : 'Start Diagnosis'}
                </Button>
                <p className="text-xs text-[#52525b]">
                  Backend configured with OpenRouter API Key.
                </p>
              </div>
              
              <div className="bg-[#09090b] rounded-lg p-4 font-mono text-xs text-[#a1a1aa] min-h-[400px] overflow-auto whitespace-pre-wrap border border-white/5 leading-relaxed">
                {aiOutput || 'Ready to start diagnosis. Click "Start Diagnosis".\n\nThe agent will:\n1. Connect to the remote server\n2. Install/Update CatPAw (if needed)\n3. Run a deep system inspection\n4. Use Hunter Alpha AI to analyze logs and metrics\n5. Provide a detailed report here.'}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Reusable Fact Card
const FactCard = ({ icon: Icon, title, children, testId, count }) => (
  <Card className="bg-[#121214] border-white/10 rounded-xl hover:border-white/20 transition-colors duration-300" data-testid={testId}>
    <CardContent className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#EF694C]" strokeWidth={1.5} />
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">{title}</h3>
        </div>
        {count !== undefined && (
          <Badge className="bg-white/5 text-[#a1a1aa] border-white/10 text-xs">{count}</Badge>
        )}
      </div>
      <div className="space-y-2">{children}</div>
    </CardContent>
  </Card>
);

// Reusable Fact Row
const FactRow = ({ label, value, mono = false, highlight = false }) => (
  <div className="flex items-center justify-between py-1 border-b border-white/5 last:border-0">
    <span className="text-xs text-[#a1a1aa]">{label}</span>
    <span className={`text-sm ${mono ? 'font-mono' : ''} ${highlight ? 'text-[#F43F5E]' : 'text-white'} text-right max-w-[200px] truncate`}>
      {value}
    </span>
  </div>
);
